import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { DomPortal, TemplatePortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
  OnInit,
  Renderer2,
  RendererFactory2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

export type TooltipPosition =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'
  | 'left'
  | 'right';

@Directive({
  selector: '[appTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnInit, OnDestroy {
  appTooltip = input.required<TemplateRef<unknown> | string | undefined>();
  position = input<TooltipPosition>('top');
  tooltipOffset = input<number>(8);

  #_overlayRef: OverlayRef | undefined;
  #textElement: HTMLElement | null = null;
  #renderer: Renderer2;

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private rendererFactory: RendererFactory2
  ) {
    this.#renderer = rendererFactory.createRenderer(null, null);
  }

  ngOnInit(): void {
    if (this.appTooltip()) {
      const position = this.overlayPositionBuilder
        .flexibleConnectedTo(this.elementRef)
        .withPositions(this.getPositions());

      this.#_overlayRef = this.overlay.create({
        positionStrategy: position,
        scrollStrategy: this.overlay.scrollStrategies.close(),
        panelClass: 'tooltip-wrapper',
      });
    }
  }

  ngOnDestroy(): void {
    this.#_overlayRef?.dispose();
  }

  @HostListener('mouseover')
  private _show(): void {
    if (this.#_overlayRef && !this.#_overlayRef.hasAttached() && this.appTooltip()) {
      if (typeof this.appTooltip() === 'string') {
        const tooltipText = (this.appTooltip() as string).replace(/\\n/g, '\n');
        const lines = tooltipText.split(/\r?\n/);
        this.#textElement = this.#renderer.createElement('div');
        lines.forEach((line, index) => {
          this.#renderer.appendChild(this.#textElement, this.#renderer.createText(line));
          if (index < lines.length - 1) {
            this.#renderer.appendChild(this.#textElement, this.#renderer.createElement('br'));
          }
        });
        this.#renderer.addClass(this.#textElement, 'tooltip-content');
        this.#renderer.addClass(this.#textElement, this.position());
        this.#renderer.appendChild(document.body, this.#textElement);
        this.#_overlayRef.attach(new DomPortal(this.#textElement));
      } else if (this.appTooltip() instanceof TemplateRef) {
        this.#_overlayRef.attach(new TemplatePortal(this.appTooltip() as TemplateRef<unknown>, this.viewContainerRef));
      }
    }
  }

  @HostListener('mouseout')
  private _hide(): void {
    this.#_overlayRef?.detach();
    if (this.#textElement) {
      this.#renderer.removeChild(document.body, this.#textElement);
      this.#textElement = null;
    }
  }

  private getPositions() {
    switch (this.position()) {
      default:
      case 'top':
        return [
          {
            originX: 'center' as const,
            originY: 'top' as const,
            overlayX: 'center' as const,
            overlayY: 'bottom' as const,
            offsetX: 0,
            offsetY: -this.tooltipOffset(),
          },
        ];
      case 'top-left':
        return [
          {
            originX: 'start' as const,
            originY: 'top' as const,
            overlayX: 'start' as const,
            overlayY: 'bottom' as const,
            offsetX: 0,
            offsetY: -this.tooltipOffset(),
          },
        ];
      case 'top-right':
        return [
          {
            originX: 'end' as const,
            originY: 'top' as const,
            overlayX: 'end' as const,
            overlayY: 'bottom' as const,
            offsetX: 0,
            offsetY: -this.tooltipOffset(),
          },
        ];
      case 'bottom':
        return [
          {
            originX: 'center' as const,
            originY: 'bottom' as const,
            overlayX: 'center' as const,
            overlayY: 'top' as const,
            offsetX: 0,
            offsetY: this.tooltipOffset(),
          },
        ];
      case 'bottom-left':
        return [
          {
            originX: 'start' as const,
            originY: 'bottom' as const,
            overlayX: 'start' as const,
            overlayY: 'top' as const,
            offsetX: 0,
            offsetY: this.tooltipOffset(),
          },
        ];
      case 'bottom-right':
        return [
          {
            originX: 'end' as const,
            originY: 'bottom' as const,
            overlayX: 'end' as const,
            overlayY: 'top' as const,
            offsetX: 0,
            offsetY: this.tooltipOffset(),
          },
        ];
      case 'left':
        return [
          {
            originX: 'start' as const,
            originY: 'center' as const,
            overlayX: 'end' as const,
            overlayY: 'center' as const,
            offsetX: -this.tooltipOffset(),
            offsetY: 0,
          },
        ];
      case 'right':
        return [
          {
            originX: 'end' as const,
            originY: 'center' as const,
            overlayX: 'start' as const,
            overlayY: 'center' as const,
            offsetX: this.tooltipOffset(),
            offsetY: 0,
          },
        ];
    }
  }
}
